const STORAGE_KEY = 'quiklearn.webgl.access-token';
const LAUNCH_STORAGE_KEY = 'quiklearn.webgl.launch-token';
const FRAGMENT_KEY = 'access_token';
const LAUNCH_FRAGMENT_KEY = 'launch_token';
const MAX_TOKEN_LENGTH = 8192;

const validToken = value =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAX_TOKEN_LENGTH &&
  !/[\s\u0000-\u001f\u007f]/.test(value);

export const captureWebAccessToken = () => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return null;
  const fragment = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
  const supplied = fragment.get(FRAGMENT_KEY);
  const launch = fragment.get(LAUNCH_FRAGMENT_KEY);
  if (supplied !== null) {
    validToken(supplied)
      ? sessionStorage.setItem(STORAGE_KEY, supplied)
      : sessionStorage.removeItem(STORAGE_KEY);
  }
  if (launch !== null) {
    validToken(launch)
      ? sessionStorage.setItem(LAUNCH_STORAGE_KEY, launch)
      : sessionStorage.removeItem(LAUNCH_STORAGE_KEY);
  }
  fragment.delete(FRAGMENT_KEY);
  fragment.delete(LAUNCH_FRAGMENT_KEY);
  const nextUrl = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(window.history.state, '', nextUrl);
  return sessionStorage.getItem(STORAGE_KEY);
};

export const getWebAccessToken = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(STORAGE_KEY);
};

export const getWebLaunchToken = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(LAUNCH_STORAGE_KEY);
};

/**
 * True when `next` differs from `current` only after the '#'. Setting such an address only changes the hash: the page
 * does not load the new launch (owner 13 Oct: "landing to unit 3 via next unit portal ... unit never loaded unless i
 * refreshed" - the next-unit launch carries the same query as the page it leaves, and its token sits in the hash).
 */
export const onlyHashDiffers = (next, current) => {
  try {
    const to = new URL(next, current);
    const from = new URL(current);
    return to.origin === from.origin && to.pathname === from.pathname && to.search === from.search;
  } catch (_) {
    return false;
  }
};

/** Opens a launch address (buildWebLaunchLocation) in this tab, loading it even when only its hash is new. */
export const goToWebLaunch = url => {
  const here = window.location.href;
  window.location.href = url;
  if (onlyHashDiffers(url, here)) window.location.reload();
};

export const buildWebLaunchLocation = (baseUrl, launchToken, queryValues = {}) => {
  if (!validToken(launchToken)) throw new Error('A valid launch token is required.');
  const origin = typeof window !== 'undefined' && window.location.origin
    ? window.location.origin
    : 'https://app.quiklearn.ai';
  const url = new URL(baseUrl, origin);
  Object.entries(queryValues).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
  url.hash = new URLSearchParams({launch_token: launchToken}).toString();
  return url.toString();
};

// ---------------------------------------------------------------------------------------------------------------------
// Keeping the session past the access token's hour (Sol fix-review R4 A): the portal alone holds the refresh token;
// this app asks the portal's /token-bridge page, in a hidden frame, for a fresh access token over postMessage - only
// the portal's exact origin is listened to - five minutes before the current one expires.
// ---------------------------------------------------------------------------------------------------------------------
const EARLY_MS = 5 * 60 * 1000;
const RETRY_MS = 60 * 1000;
const BRIDGE_TIMEOUT_MS = 15000;

/** When an access token expires (ms since epoch), from its `exp` claim; 0 when unreadable. */
export const tokenExpiresAt = token => {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return 0;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
    return Number(json?.exp) > 0 ? Number(json.exp) * 1000 : 0;
  } catch (_) {
    return 0;
  }
};

/** Asks the portal's bridge for a fresh access token; resolves the token, or null (signed out, refused, no answer). */
export const requestBridgeToken = (bridgeUrl, doc = document, win = window, timeoutMs = BRIDGE_TIMEOUT_MS) =>
  new Promise(resolve => {
    let bridgeOrigin;
    try {
      bridgeOrigin = new URL(bridgeUrl).origin;
    } catch (_) {
      resolve(null);
      return;
    }
    const id = `t${Date.now()}${Math.random().toString(16).slice(2, 8)}`;
    const frame = doc.createElement('iframe');
    frame.style.display = 'none';
    frame.setAttribute('aria-hidden', 'true');
    let timer = null;
    const finish = token => {
      win.removeEventListener('message', onMessage);
      clearTimeout(timer);
      if (frame.parentNode) frame.parentNode.removeChild(frame);
      resolve(validToken(token) ? token : null);
    };
    const onMessage = event => {
      if (event.origin !== bridgeOrigin) return;
      const data = event.data || {};
      if (data.type === 'ql-token-bridge-ready') {
        frame.contentWindow && frame.contentWindow.postMessage({type: 'ql-token-request', id}, bridgeOrigin);
      } else if (data.type === 'ql-token' && data.id === id) {
        finish(data.accessToken);
      }
    };
    win.addEventListener('message', onMessage);
    timer = setTimeout(() => finish(null), timeoutMs);
    frame.src = bridgeUrl;
    doc.body.appendChild(frame);
  });

let keepAliveTimer = null;

/** Starts (once) the refresh loop for this tab's session; a no-op without a token or outside a browser. */
export const startWebSessionKeepAlive = (portalBaseUrl, now = () => Date.now()) => {
  if (keepAliveTimer || typeof window === 'undefined' || typeof document === 'undefined') return;
  const bridgeUrl = `${String(portalBaseUrl || '').replace(/\/$/, '')}/token-bridge`;
  const schedule = delayMs => {
    keepAliveTimer = setTimeout(tick, Math.max(1000, delayMs));
  };
  const tick = async () => {
    const token = getWebAccessToken();
    if (!token) {
      keepAliveTimer = null;
      return;
    }
    const expiresAt = tokenExpiresAt(token);
    if (expiresAt && expiresAt - now() > EARLY_MS) {
      schedule(expiresAt - now() - EARLY_MS);
      return;
    }
    const fresh = await requestBridgeToken(bridgeUrl);
    if (fresh) sessionStorage.setItem(STORAGE_KEY, fresh);
    const next = tokenExpiresAt(fresh || token);
    schedule(fresh && next ? next - now() - EARLY_MS : RETRY_MS);
  };
  tick();
};
